import React, {
  Suspense,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from 'react'
import { act, Canvas, useFrame, useThree } from '@react-three/fiber'
import { useSpring, useTransition } from '@react-spring/core'
import { animated, config } from '@react-spring/three'
import {
  useGLTF,
  softShadows,
  useHelper,
  OrbitControls,
  Stats,
  useTexture,
  Environment,
  MeshWobbleMaterial,
  ScrollControls,
  useScroll,
  Text,
  Sky,
  Html
} from '@react-three/drei'
import styles from './scrollCanvas.m.scss'
import {
  Mesh,
  Vector3,
  Quaternion,
  Scene,
  SpotLightHelper,
  Color,
  TextureLoader,
  MeshPhysicalMaterial,
  DoubleSide,
  FlatShading,
  MeshPhongMaterial,
  UniformsUtils,
  BackSide,
  MeshStandardMaterial,
  Float32BufferAttribute,
  Vector2,
  Spherical,
  AdditiveBlending,
  Box3
} from 'three'
import {
  GLTF,
  SubsurfaceScatteringShader,
  EffectComposer,
  ShaderPass,
  RenderPass
} from 'three-stdlib'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { Leva, useControls } from 'leva'
import { NoiseEffect } from 'components/noiseEffect'
import {
  EffectComposerTest,
  EffectComposerTests
} from 'components/effectComposerTests'
import { ParticlesEffect } from 'components/particlesEffect'
import { EnvSphereShader } from 'components/envSphereShader'
import { useScrollPosition } from '../../hooks/useScrollPosition'
import { InlineMutation } from 'components/inlineMutation'
import { aminoAcids } from '../aminoAcids.js'
import { usePctChange } from '../store'

// Extends GLTF type with the one useGLTF will return
type GLTFResult = GLTF & {
  nodes: {
    protein: THREE.Mesh
  }
  materials: {
    bake: THREE.MeshStandardMaterial
  }
}

const isMobile = window.innerWidth < 768

const ScreenPositionedElement = () => {
  const ref = useRef<THREE.Mesh>()
  const boundingBox = ref.computeBoundingBox() || null
  const screenPosition = {
    top: 0,
    left: 0
  }
  const threePosition = {
    top: screenPosition.top,
    left: screenPosition.left
  }

  console.log(boundingBox)

  return (
    <group>
      <mesh ref={ref} position={[0, 0, 0]}>
        <sphereBufferGeometry attach='geometry' args={[1, 32, 32]} />
        <meshStandardMaterial
          emissiveIntensity={1}
          attach='material'
          color={0xff0000}
        />
      </mesh>
    </group>
  )
}

const GetScreenPosition = ({ p, ref }) => {
  const { camera } = useThree()
  const { width, height } = useThree().size
  const widthHalf = width / 2
  const heightHalf = height / 2

  console.log(p)

  useFrame(() => {
    if (ref && ref.current) {
      const pos = new Vector3(p[0], p[1], p[2]) // object.position.clone()
      pos.project(camera)
      pos.x = pos.x * widthHalf + widthHalf
      pos.y = -(pos.y * heightHalf) + heightHalf

      ref.current.style.transform =
        'translate(' + pos.x + 'px, ' + pos.y + 'px)'
    }
  })

  return null
}

// Camera handler
const CameraAnimation = ({ cameraData, stage }: { cameraData: number[] }) => {
  const [values, setValues] = useState(cameraData)
  const { camera, scene } = useThree()
  const spring = useSpring({
    to: {
      position: values
    },
    onChange: {
      position: value => {
        camera.position.x = value[0]
        camera.position.y = value[1]
        camera.position.z = value[2]
        camera.lookAt(value[3], value[4], value[5])
      }
    },
    config: { ...config.default, friction: stage.id === 10 ? 200 : 90 }
  })

  // console.log('New dolly', values)

  usePctChange(pct => {
    if (stage.id === 9) {
      const cameraStage = Math.floor(50 * pct)
      const seqId =
        stage.direction === 'down' ? cameraStage + 45 : 46 + (46 + cameraStage)

      //  console.log(cameraStage, seqId)
      // console.log(cameraStage, seqId)

      const target = scene.children[0].children.find(
        c => c.name.split('-')[0] === seqId.toString()
      )
      if (target) {
        const cameraPosition = target.position.clone()
        cameraPosition.x -= 6
        cameraPosition.y += 1
        // cameraPosition.z *= -1

        setValues([...cameraPosition, ...target.position])
      }
    }
  })

  useEffect(() => {
    console.log('Set camera position')

    camera.position.x = values[0]
    camera.position.y = values[1]
    camera.position.z = values[2]

    camera.lookAt(values[3], values[4], values[5])
  }, [])

  useEffect(() => {
    setValues(cameraData)
  }, [cameraData])

  return null
}

const AnimatedLights = ({
  position,
  color,
  intensity
}: {
  position: SpringValue<number>
  color: Color[]
  intensity: number[]
}) => {
  const ref = useRef(null)
  const refPl1 = useRef(null)
  const refPl2 = useRef(null)
  const refPl1Helper = useRef(null)
  const refPl2Helper = useRef(null)
  const [plight1, setpl1] = useState()
  const [plight2, setpl2] = useState()
  const [dlight, setdl] = useState()
  // const posx = position.to([0, 1], [0, 20])

  useFrame((state, delta) => {
    const radiusV2 = Math.sqrt(
      Math.pow(state.camera.position.x, 2) +
        Math.pow(state.camera.position.y, 2) +
        Math.pow(state.camera.position.z, 2)
    )
    const headingV2 = Math.atan2(
      state.camera.position.x,
      state.camera.position.z
    )
    const pitchV2 = Math.asin(-state.camera.position.y / radiusV2)

    const s1 = new Spherical(
      200,
      (-((pitchV2 / Math.PI) * 180 - 90 + position[0][0]) * Math.PI) / 180,
      (((headingV2 / Math.PI) * 180 - 180 + position[0][1]) * Math.PI) / 180
    )
    const s2 = new Spherical(
      200,
      (-((pitchV2 / Math.PI) * 180 - 90 + position[1][0]) * Math.PI) / 180,
      (((headingV2 / Math.PI) * 180 - 180 + position[1][1]) * Math.PI) / 180
    )
    // console.log(headingV2 / Math.PI * 180, pitchV2 / Math.PI * 180)
    // state.camera.setViewOffset( window.innerWidth, window.innerHeight, 200, 0, window.innerWidth, window.innerHeight );

    refPl1.current.position.setFromSpherical(s1)
    refPl2.current.position.setFromSpherical(s2)

    // refPl1Helper.current.position.set(
    //   refPl1.current.position.x,
    //   refPl1.current.position.y,
    //   refPl1.current.position.z
    // )
    // refPl2Helper.current.position.set(refPl2.current.position.x, refPl2.current.position.y, refPl2.current.position.z)
  })

  console.log(color)

  return (
    <animated.group ref={ref}>
      {/* <ambientLight intensity={1} color='#002b8f' /> */}
      {/* <hemisphereLight intensity={1} color='red' groundColor='blue' /> */}
      {/* <directionalLight ref={setdl} intensity={1} position={[-20, 30, 20]} /> */}
      <pointLight
        ref={refPl1}
        color={color[0]}
        intensity={intensity[0]}
        distance={300}
      />
      <pointLight
        ref={refPl2}
        color={color[1]}
        intensity={intensity[1]}
        distance={700}
      />
      {/* <pointLight ref={refPl2} /> */}
      {/* <pointLight ref={setpl2} position={[20, -30, 20]} /> */}
      {/* {refPl1.current && <pointLightHelper args={[refPl1.current, 1]} />} */}
      {/* <mesh ref={refPl1Helper}>
        <boxBufferGeometry attach='geometry' args={[10, 10, 10]} />
        <meshBasicMaterial wireframe color='white' />
      </mesh> */}
      {/* <mesh ref={refPl2Helper} >
        <boxBufferGeometry
          attach='geometry'
          args={[10, 10, 10]}
        />
        <meshBasicMaterial wireframe color='white' />
      </mesh> */}
      {/* {dlight && <directionalLightHelper args={[dlight, 1]} />} */}
    </animated.group>
  )
}

const Virus = ({ show }) => {
  const ref = useRef()
  const virus = useGLTF(require('./virus-b.glb'))
  const model = useMemo(
    () => ({
      body: virus.nodes.body,
      spike: virus.nodes.spike
    }),
    [virus]
  )
  console.log(virus)
  const shader = SubsurfaceScatteringShader
  const uniforms = UniformsUtils.clone(shader.uniforms)
  const texture = useTexture(require('./virus_bake.jpg'))
  texture.flipY = false

  uniforms.diffuse.value = new Vector3(1.0, 0.0, 0.2)
  uniforms.shininess.value = 50
  uniforms.map.value = texture
  uniforms.thicknessMap.value = texture
  uniforms.thicknessColor.value = new Vector3(0.53, 0.53, 0.53)
  uniforms.thicknessDistortion.value = 0.1
  uniforms.thicknessAmbient.value = 0.4
  uniforms.thicknessAttenuation.value = 0.8
  uniforms.thicknessPower.value = 2
  uniforms.thicknessScale.value = 16

  const transition = useTransition(show, {
    config: {
      duration: 250
    },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  useFrame(({ clock }) => {
    if (ref.current && show) {
      ref.current.position.y += Math.sin(clock.getElapsedTime()) * 0.05
    }
  })

  return transition(
    (styles, item) =>
      item && (
        <group ref={ref}>
          <animated.mesh geometry={model.spike.geometry} position={[0, 0, -20]}>
            <Html position={[0, 60, 0]} center>
              <div className='main-label'>
                <div className='arrow' />
                Protéine Spike
              </div>
              <div className='spike-label-circle' />
            </Html>
            <animated.shaderMaterial
              transparent
              extensions={['derivatives']}
              lights
              attach='material'
              uniforms={uniforms}
              uniforms-opacity-value={styles.opacity}
              fragmentShader={shader.fragmentShader}
              vertexShader={shader.vertexShader}
            />
            {/* <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              // transparent
              // opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              // emissiveIntensity={1}
              // emissive='#ffffff'
              attach='material'
              color={0x222222} */}
            {/* <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              transparent
              opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              // emissiveIntensity={1}
              // emissive='#ffffff'
              attach='material'
              color={0x222222}
            /> */}
            {/* <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              // transparent
              // opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              emissiveIntensity={1}
              emissive='#ff0000'
              attach='material'
              color={0xffaa00}
            /> */}
          </animated.mesh>
          <animated.mesh geometry={model.body.geometry} position={[0, 0, -20]}>
            {/* <animated.shaderMaterial
              transparent
              extensions={['derivatives']}
              lights
              attach='material'
              uniforms={uniforms}
              uniforms-opacity-value={styles.opacity}
              fragmentShader={shader.fragmentShader}
              vertexShader={shader.vertexShader}
            /> */}
            {/* <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              // transparent
              // opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              // emissiveIntensity={1}
              // emissive='#ffffff'
              attach='material'
              color={0x222222} */}
            <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              transparent
              opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              // emissiveIntensity={1}
              // emissive='#ffffff'
              attach='material'
              color={0x222222}
            />
            {/* <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              // transparent
              // opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              emissiveIntensity={1}
              emissive='#ff0000'
              attach='material'
              color={0xffaa00}
            /> */}
          </animated.mesh>
        </group>
      )
  )
}

const Mutation = ({ i, position, maxScale }) => {
  const material = useRef()
  const mesh = useRef()
  const [rd, setRd] = useState(Math.random() * 5)

  useFrame(({ clock }) => {
    const scale =
      ((Math.sin(rd + clock.getElapsedTime() * 5) + 1) / 2) * maxScale
    material.current.opacity =
      (Math.sin(rd + clock.getElapsedTime() * 5) + 1) / 2
    mesh.current.scale.set(scale, scale, scale)
  })

  return (
    <mesh position={position} ref={mesh} scale={0}>
      <sphereBufferGeometry attach='geometry' args={[1.5, 32, 32]} />
      {/* <Html>
    {i}
  </Html> */}
      <meshBasicMaterial
        ref={material}
        // map={uniformsValues.thicknessMap.value}
        // aoMap={uniformsValues.thicknessMap.value}
        transparent
        opacity={0}
        // opacity={styles.opacity.to([0, 1], [0, 1])}
        // opacity={0.5}
        // emissiveIntensity={1}
        // emissive='#ffffff'
        attach='material'
        color={0xffffff}
        blending={AdditiveBlending}
      />
    </mesh>
  )
}

const Mutations = ({ show = true }) => {
  const ref = useRef()
  // const mutations = useGLTF(require('./mutations.glb'))
  // const model = useMemo(() => virus.nodes.body, [virus])
  const locations = [
    [0, 20, 0],
    [3, 20, 10],
    [-10, 17, 0],
    [5, 16, 5],
    [-5, 22, 4],
    [2, 3, 10],
    [-2, 6, 10],
    [-6, -5, -10],
    [-9, 3, 8],
    [-10, 10, -18]
  ]

  const transition = useTransition(show, {
    config: config.molasses,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  // useFrame(({ clock }) => {
  //   ref.current.position.y += Math.sin(clock.getElapsedTime()) * 0.05
  // })

  return transition(
    (styles, item) =>
      item && (
        <group>
          {locations.map((position, i) => (
            <Mutation
              key={`mutation-${i}`}
              position={position}
              i={i}
              maxScale={1}
            />
          ))}
          {/* <animated.mesh
            geometry={model.geometry}
            position={[0, 0, 0]}
            ref={ref}
          >
            <animated.meshStandardMaterial
              // map={uniformsValues.thicknessMap.value}
              // aoMap={uniformsValues.thicknessMap.value}
              // transparent
              // opacity={styles.opacity.to([0, 1], [0, 1])}
              // opacity={0.1}
              // emissiveIntensity={1}
              // emissive='#ffffff'
              attach='material'
              color={0x222222}
            />
          </animated.mesh> */}
        </group>
      )
  )
}

const SpikeProteinElement = ({
  model,
  texturePath,
  textureId = 0,
  position,
  diffuseValue = new Vector3(1.0, 0.0, 0.2),
  shininessValue = 500.0,
  thicknessColorValue = new Vector3(1.0, 0.0, 0.0),
  thicknessDistortionValue = 0.1,
  thicknessAmbientValue = 0.4,
  thicknessAttenuationValue = 0.8,
  thicknessPowerValue = 2.0,
  thicknessScaleValue = 16.0,
  show = true,
  label = null
}) => {
  // TODO: Gradient texture?
  // https://blender.stackexchange.com/questions/181641/get-this-gradient-style-in-blender

  // console.log('> > > Render spike protein element', model.name, show)

  const textures = useTexture(texturePath)
  const whiteTexture = useTexture(require('./white.jpg'))

  // const bs = useRef()
  const shader = SubsurfaceScatteringShader
  const uniforms = UniformsUtils.clone(shader.uniforms)
  const materialRef = useRef(null)
  const [uniformsValues, setUniformsValues] = useState(uniforms)
  const transitions = useTransition(show, {
    config: {
      duration: 250
      // mass: 1.6,
      // tension: 323,
      // friction: 30,
      // precision: 0.202
    },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  const labelTransition = useTransition(label, {
    config: config.molasses,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  useEffect(() => {
    console.log('Load Texture')

    // new TextureLoader().load(texturePath, texture => {
    //   console.log('Texture loaded', texturePath)
    //   texture.flipY = false

    //   // uniforms.map.value = texture
    //   uniforms.diffuse.value = diffuseValue
    //   uniforms.shininess.value = shininessValue

    //   uniforms.thicknessMap.value = texture
    //   uniforms.thicknessColor.value = thicknessColorValue
    //   uniforms.thicknessDistortion.value = thicknessDistortionValue
    //   uniforms.thicknessAmbient.value = thicknessAmbientValue
    //   uniforms.thicknessAttenuation.value = thicknessAttenuationValue
    //   uniforms.thicknessPower.value = thicknessPowerValue
    //   uniforms.thicknessScale.value = thicknessScaleValue

    //   if (materialRef.current) {
    //     materialRef.current.uniforms.shininess.value = Math.random() * 500
    //   }

    //   console.log(texture.image.currentSrc)

    //   setUniformsValues({ ...uniforms })
    // })

    textures.map(texture => {
      texture.flipY = false
      texture.needsUpdate = true
    })

    uniforms.diffuse.value = diffuseValue
    uniforms.shininess.value = shininessValue
    uniforms.map.value = whiteTexture
    uniforms.thicknessMap.value = textures[textureId]
    uniforms.thicknessColor.value = thicknessColorValue
    uniforms.thicknessDistortion.value = thicknessDistortionValue
    uniforms.thicknessAmbient.value = thicknessAmbientValue
    uniforms.thicknessAttenuation.value = thicknessAttenuationValue
    uniforms.thicknessPower.value = thicknessPowerValue
    uniforms.thicknessScale.value = thicknessScaleValue
  }, [textures])

  // if (bs.current) {
  //   bs.current.map = uniformsValues.thicknessMap.value
  //   // bs.current.aoMap = uniformsValues.thicknessMap.value
  //   // bs.current.aoMapIntensity = 0.5
  //   bs.current.needsUpdate = true
  // }

  useEffect(() => {
    // uniforms.thicknessColor.value = thicknessColorValue
    // uniforms.diffuse.value = diffuseValue

    // console.log('new color', model.name, thicknessColorValue)
    // setUniformsValues({ ...uniforms })

    if (materialRef.current) {
      materialRef.current.uniforms.thicknessColor.value = thicknessColorValue
      materialRef.current.uniforms.diffuse.value = diffuseValue
    }
  }, [thicknessColorValue])

  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.thicknessMap.value = textures[textureId]
    }
  }, [textureId])

  return transitions(
    (styles, item) =>
      item && (
        <group>
          <animated.mesh geometry={model.geometry} position={position}>
            {/* {labelTransition((labelStyle, labelItem) => labelItem && ( */}
            {label && (
              <Html center position={[0, 30, 0]} style={{ opacity: 1 }}>
                <div className='main-label'>
                  <div className='arrow' />
                  {label.text}
                </div>
              </Html>
            )}
            {/* )} */}
            {/* <animated.meshStandardMaterial
            ref={bs}
            // map={uniformsValues.thicknessMap.value}
            // aoMap={uniformsValues.thicknessMap.value}
            transparent
            opacity={model.name==='segment' ? 1 : 0.1}
            // opacity={styles.opacity.to([0, 1], [0, 1])}
            attach='material'
            color={0x2222aa}
            /> */}
            <animated.shaderMaterial
              transparent
              extensions={['derivatives']}
              ref={materialRef}
              lights
              attach='material'
              uniforms={uniformsValues}
              uniforms-opacity-value={styles.opacity}
              fragmentShader={shader.fragmentShader}
              vertexShader={shader.vertexShader}
            />
          </animated.mesh>
        </group>
      )
  )
}

const SpikeProtein = ({
  position,
  chainsColor,
  autoAnimate,
  stage
}: {
  position: SpringValue<number>
  chainsColor: []
  autoAnimate: boolean
  stage: {
    id: number
    direction: 'up' | 'down'
  }
}) => {
  // console.log('> > Render spike protein')
  const [activeAminoAcid, setActiveAminoAcid] = useState(46)
  const ref = useRef()
  const scrollOffset = useRef(0)
  const { viewport, scene, camera } = useThree()
  // const boundingBox = ref.computeBoundingBox() || null
  const spikeProtein = useGLTF(require('./chains_abcnag_simp_bool_draco.glb'))
  const chainSegment = useGLTF(
    require('./chain_C-amino-acids_segment-draco.glb')
  )
  const meshes = useMemo(
    () => ({
      chainA: spikeProtein.nodes.chain_a,
      chainB: spikeProtein.nodes.chain_b,
      chainC: spikeProtein.nodes.chain_c,
      nag: spikeProtein.nodes.nag
    }),
    [spikeProtein]
  )
  const chainSegmentMesh = useMemo(() => chainSegment.nodes.segment, [
    chainSegment
  ])
  const chainSegmentTargetsObjects = useMemo(
    () =>
      Object.keys(chainSegment.nodes)
        .filter(key => !['Scene', 'segment'].includes(key))
        .map(key => chainSegment.nodes[key]),
    [chainSegment]
  )

  // console.log(chainSegmentTargetsObjects)

  // const posx = position.to([0, 1], [0, 20])
  const [{ posy }, apiTranslate] = useSpring(() => ({
    posy: 0
  }))

  const posXChainA = [5].includes(stage.id)
    ? -35
    : [6].includes(stage.id)
    ? 0
    : [7, 8].includes(stage.id)
    ? -90
    : 0
  const posXChainB = [5].includes(stage.id)
    ? 0
    : [6].includes(stage.id)
    ? 50
    : [7, 8].includes(stage.id)
    ? -60
    : 0
  const posXChainC = [5].includes(stage.id)
    ? 30
    : [6].includes(stage.id)
    ? 80
    : [7, 8].includes(stage.id)
    ? -9
    : 0

  const posZChainA = [5].includes(stage.id)
    ? 0
    : [6].includes(stage.id)
    ? 0
    : [7, 8].includes(stage.id)
    ? -60
    : 0
  const posZChainB = [5].includes(stage.id)
    ? 0
    : [6].includes(stage.id)
    ? -30
    : [7, 8].includes(stage.id)
    ? -30
    : 0
  const posZChainC = [5].includes(stage.id)
    ? 0
    : [6].includes(stage.id)
    ? -60
    : [7, 8].includes(stage.id)
    ? 0
    : 0

  const [{ posChainA, posChainB, posChainC }] = useSpring(
    {
      posChainA: [posXChainA, 0, posZChainA],
      posChainB: [posXChainB, 0, posZChainB],
      posChainC: [posXChainC, 0, posZChainC],
      config: { mass: 5, tension: 150, friction: 50, precision: 0.0001 }
    },
    [stage]
  )

  useFrame(({ clock }) => {
    if (ref.current) {
      if (autoAnimate) {
        ref.current.rotation.y =
          (ref.current.rotation.y + 0.005) % (2 * Math.PI)
      } else {
        ref.current.rotation.y += (0 - ref.current.rotation.y) * 0.01
      }

      ref.current.position.y += Math.sin(clock.getElapsedTime()) * 0.01
    }
  })

  usePctChange(pct => {
    if (stage.id === 9) {
      const cameraStage = Math.floor(50 * pct)
      const seqId =
        stage.direction === 'down' ? cameraStage + 45 : 46 + (46 + cameraStage)

      console.log(activeAminoAcid, seqId)

      if (activeAminoAcid !== seqId) {
        console.log('Goto to new amo acid', seqId)
        setActiveAminoAcid(seqId)
      }
    }
  })

  const handleScroll = useCallback(() => {
    const viewportOffset =
      ((window.scrollY - scrollOffset.current) * viewport.height) / 2
    let offset = 0

    // Cover animation
    if (stage.id === 1) {
      // console.log(boundingBox.current, stage.container)

      if (stage.direction === 'down') {
        offset = viewportOffset * 0.05
      } else {
        offset = ((window.scrollY * viewport.height) / 2) * 0.05
        if (offset < 0) {
          offset = 0
        }
      }

      apiTranslate.start({
        posy: offset
      })

      // Second apparition
    } else if (stage.id === 2) {
      if (stage.direction === 'down') {
        offset = -100 + viewportOffset * 0.05

        if (offset > 0) {
          offset = 0
        }
      } else {
        offset = 0 + viewportOffset * 0.05
      }

      apiTranslate.start({
        posy: offset
      })
    }
  }, [stage])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [stage])

  useEffect(() => {
    scrollOffset.current = window.scrollY

    if (stage.id === 2) {
      apiTranslate.set({
        posy: stage.direction === 'down' ? -100 : 0
      })
    } else if (stage.id === 1) {
      apiTranslate.set({
        posy: stage.direction === 'down' ? 0 : 115
      })
    } else if (stage.id > 2) {
      apiTranslate.set({
        posy: 0
      })
    }
  }, [stage])

  const labelChainA =
    stage.id === 2
      ? {
          text: 'Protéine Spike'
        }
      : [5, 6, 7].includes(stage.id)
      ? { text: 'Chaîne A' }
      : null

  const mutation = chainSegmentTargetsObjects.find(
    t => t.name.split('-')[0] === '73'
  )

  return (
    <animated.group position-y={posy.to(pos => pos)} ref={ref}>
      {/* {stage.id === 11 && (
        <Html position={[10, -5, 0]}>
          <div style={{ width: '150px' }}>
            <InlineMutation protein='s' from='A' pos={60} to='L' arrow />
          </div>
        </Html>
      )} */}

      {[13, 14].includes(stage.id) && (
        <Html position={[-5, 30, 0]} center style={{ opacity: 1 }}>
          <div className='main-label'>
            <div className='arrow' />
            Récepteur cellulaire
            {[13].includes(stage.id) && <div className='spike-label-circle' />}
          </div>
        </Html>
      )}

      <Suspense fallback={null}>{stage.id === 11 && <Mutations />}</Suspense>

      <SpikeProteinElement
        model={meshes.chainA}
        texturePath={[
          require('./chain_a_bake_single.jpg'),
          require('./chain_a_bake-2_wonag.jpg')
        ]}
        textureId={[5, 6, 7].includes(stage.id) ? 0 : 1}
        diffuseValue={chainsColor.diffuse[0]}
        thicknessColorValue={chainsColor.thickness[0]}
        position={posChainA}
        show={![3, 8, 9, 10].includes(stage.id)}
        label={labelChainA}
      />
      <SpikeProteinElement
        model={meshes.chainB}
        texturePath={[
          require('./chain_b_bake_single.jpg'),
          require('./chain_b_bake-2_wonag.jpg')
        ]}
        textureId={[5, 6, 7].includes(stage.id) ? 0 : 1}
        diffuseValue={chainsColor.diffuse[1]}
        thicknessColorValue={chainsColor.thickness[1]}
        position={posChainB}
        show={![3, 8, 9, 10].includes(stage.id)}
        label={[5, 6, 7].includes(stage.id) ? { text: 'Chaîne B' } : null}
      />
      <SpikeProteinElement
        model={meshes.chainC}
        texturePath={[
          require('./chain_c_bake_single.jpg'),
          require('./chain_c_bake-2_wonag.jpg')
        ]}
        textureId={[5, 6, 7].includes(stage.id) ? 0 : 1}
        diffuseValue={chainsColor.diffuse[2]}
        thicknessColorValue={chainsColor.thickness[2]}
        position={posChainC}
        show={![3, 8, 9, 10].includes(stage.id)}
        label={[5, 6, 7].includes(stage.id) ? { text: 'Chaîne C' } : null}
      />
      <SpikeProteinElement
        model={meshes.nag}
        texturePath={[require('./nag_bake-2.jpg')]}
        diffuseValue={new Vector3(1.0, 0.0, 0.2)}
        thicknessColorValue={new Vector3(0.8, 0.7, 0.6)}
        position={[0, 0, 0]}
        show={[14, 15, 16, 17].includes(stage.id)}
      />
      <SpikeProteinElement
        model={chainSegmentMesh}
        texturePath={[require('./chain_C-segment-bake.jpg')]}
        diffuseValue={chainsColor.diffuse[2]}
        thicknessColorValue={chainsColor.thickness[2]}
        position={[0, 0, 0]}
        show={[8, 9, 10].includes(stage.id)}
      />
      {stage.id === 10 && (
        <Mutation
          position={[
            mutation.position.x - 13.5,
            mutation.position.y - 6.85,
            mutation.position.z
          ]}
          maxScale={0.5}
        />
      )}
      {[9].includes(stage.id) &&
        chainSegmentTargetsObjects.map(target => (
          <mesh
            // geometry={target.geometry}
            position={[
              target.position.x - 13.5,
              target.position.y - 6.85,
              target.position.z
            ]}
            key={`target-${target.name}`}
            name={target.name}
          >
            {activeAminoAcid === +target.name.split('-')[0] && (
              <Html
                center
                position={[0, 0.5, 0]}
                style={{ pointerEvents: 'none' }}
              >
                <div className='main-label'>
                  <div className='arrow' />
                  <span className='num-label'>
                    {target.name.split('-')[0]}
                  </span>{' '}
                  {
                    aminoAcids.find(
                      a =>
                        a.abbr.toLowerCase() ===
                        target.name.split('-')[1].toLowerCase()
                    ).name
                  }
                </div>
              </Html>
            )}
            <meshBasicMaterial wireframe color='white' />
          </mesh>
        ))}
      {stage.id === 10 && (
        <mesh
          // geometry={target.geometry}
          position={[
            mutation.position.x - 13.5,
            mutation.position.y - 6.85,
            mutation.position.z
          ]}
          key={`target-${mutation.name}`}
          name={mutation.name}
        >
          {/* <sphereBufferGeometry attach="geometry" args={target.position} /> */}
          <Html position={[0, 2.2, 0]} center>
            <div className='main-label'>
              <div className='arrow' />
              Mutation
            </div>
          </Html>
          <meshBasicMaterial wireframe color='white' />
        </mesh>
      )}
    </animated.group>
  )
}

const MemoizedSpike = React.memo(SpikeProtein)

export const ScrollCanvas = ({
  stage
}: {
  stage: {
    id: number
    direction: 'up' | 'down'
  }
}) => {
  console.log('> Render scene', stage)
  const [autoAnimateModel, setAutoAnimateModel] = useState(true)
  const [cameraPosition, setCameraPosition] = useState([
    0,
    20,
    isMobile ? 90 : 75
  ])
  const [cameraTarget, setCameraTarget] = useState([0, -10, 0])
  const { environment } = useControls({
    environment: { value: 'dawn', options: ['sunset', 'dawn', 'night'] }
  })

  const { colorTA, colorTB, colorTC } = useControls('Thickness color', {
    colorTA: { value: { r: 255, g: 50, b: 50 }, label: 'Chain A' },
    colorTB: { value: { r: 255, g: 50, b: 50 }, label: 'Chain B' },
    colorTC: { value: { r: 255, g: 50, b: 50 }, label: 'Chain C' }
  })

  const { colorDA, colorDB, colorDC } = useControls('Diffuse color', {
    colorDA: { value: { r: 122, g: 70, b: 0 }, label: 'Chain A' },
    colorDB: { value: { r: 122, g: 70, b: 0 }, label: 'Chain B' },
    colorDC: { value: { r: 122, g: 70, b: 0 }, label: 'Chain C' }
  })

  const { lightAPos, lightBPos } = useControls('Light position', {
    lightAPos: { value: { x: 50, y: 70 }, label: 'Light A' },
    lightBPos: { value: { x: -50, y: -70 }, label: 'Light B' }
  })

  const { lightAColor, lightBColor } = useControls('Light color', {
    lightAColor: { value: { r: 136, g: 136, b: 136 }, label: 'Light A' },
    lightBColor: { value: { r: 136, g: 136, b: 0 }, label: 'Light B' }
  })

  const { lightAIntensity, lightBIntensity } = useControls('Light intensity', {
    lightAIntensity: { value: 7, label: 'Light A' },
    lightBIntensity: { value: 1, label: 'Light B' }
  })

  const [{ pos }] = useSpring(
    {
      pos: 0,
      config: { mass: 5, tension: 1000, friction: 50, precision: 0.0001 }
    },
    [stage]
  )

  const GradientVertexColor = ({ size, position }: ShadowProps) => {
    console.log('Render gradient')
    const geometryRef = useRef(null)

    useEffect(() => {
      const positionAttribute = geometryRef.current.getAttribute('position')
      const colors = []
      const color = new Color()

      for (let i = 0; i < positionAttribute.count; i += 3) {
        color.set(Math.random() * 0xffffff)

        colors.push(color.r, color.g, color.b)
        colors.push(color.r, color.g, color.b)
        colors.push(color.r, color.g, color.b)
      }

      geometryRef.current.setAttribute(
        'color',
        new Float32BufferAttribute(colors, 3)
      )
    }, [geometryRef])

    return (
      <mesh
        position={[position[0], position[1], position[2]]}
        rotation={[0, 0, 0]}
        renderOrder={1}
      >
        <boxBufferGeometry
          ref={geometryRef}
          attach='geometry'
          args={[200, 200, 200, 1, 1, 1]}
        />
        <meshBasicMaterial side={BackSide} vertexColors />
      </mesh>
    )
  }

  const Gradient = ({ size, position }: ShadowProps) => {
    const geometryRef = useRef(null)

    // Mix two materials
    // https://github.com/flolu/threejs-noise-scaling/blob/master/index.js

    // Simple gradient shader
    const vertexShader = `
      varying vec3 pos; 
      vec4 matRowA;
      vec4 matRowB;
      vec4 matRowC;
      vec4 matRowD;

      void main() {
        pos = position.xyz;
        matRowA = modelViewMatrix[0];
        matRowB = modelViewMatrix[1];
        matRowC = modelViewMatrix[2];
        matRowD = modelViewMatrix[3];
        matRowA = vec4(matRowA.x, matRowA.y, matRowA.z, 0.0);
        matRowB = vec4(matRowB.x, matRowB.y, matRowB.z, 0.0);
        matRowC = vec4(matRowC.x, matRowC.y, matRowC.z, 0.0);
        matRowD = vec4(0.0, 0.0, 0.0, 0.05);
        gl_Position = projectionMatrix * mat4(matRowA, matRowB, matRowC, matRowD) * vec4(position, 1.0);
      }
    `

    const fragmentShader = `
      uniform vec3 colors[2]; 

      varying vec3 pos;

      void main() {
        float f = (pos.y + 100.) / 200.;  // linear interpolation
        // but you can also use 'smoothstep'
        f = clamp(f, 0., 1.);
        gl_FragColor = vec4(mix(colors[0], colors[1], f), 1.0);
      }
    `

    const uniforms = {
      colors: {
        type: 'v3v',
        value: [new Color('#222233'), new Color('#332233')]
        // value: [new Color('#0000ff'), new Color('#ff0000')]
      }
    }

    return (
      // <mesh
      //   position={[position[0], position[1], position[2]]}
      //   rotation={[-Math.PI / 2, 0, 0]}
      //   renderOrder={1}
      // >
      //   <planeBufferGeometry attach='geometry' args={[size[0], size[1]]} />
      //   <shaderMaterial
      //     transparent
      //     // opacity={0.5}
      //     attach='material'
      //     uniforms={uniforms}
      //     fragmentShader={fragmentShader}
      //     vertexShader={vertexShader}
      //     // blending={THREE.MultiplyBlending}
      //   />
      // </mesh>
      <mesh
        position={[position[0], position[1], position[2]]}
        rotation={[0, 0, 0]}
        renderOrder={1}
      >
        <boxBufferGeometry
          ref={geometryRef}
          attach='geometry'
          args={[200, 200, 200, 10, 10, 10]}
        />
        {/* <meshBasicMaterial onBeforeCompile={shader => {
        shader.vertexShader = `
        varying vec2 vUv;
        ${shader.vertexShader}
      `.replace(
        `#include <begin_vertex>`,
        `#include <begin_vertex>
      vPos = vec3(position * scaling);
      `)
      shader.fragmentShader = `
        varying vec2 vUv;
        ${shader.fragmentShader}      
      `.replace(
          `vec4 diffuseColor = vec4( diffuse, opacity );`,
          `
          float dist = length(2.0 * (vUv - 0.5));
          float colorMix = smoothstep(1.0, 0.0, dist);
          vec4 c = vec4(mix(vec3(0.0, 0.0, 0.1), vec3(0.1, 0.0, 0.0), colorMix), 1.0);

        vec4 diffuseColor = c;`
        )
      }}
      /> */}
        <shaderMaterial
          transparent
          depthWrite={false}
          side={BackSide}
          // opacity={0.5}
          attach='material'
          uniforms={uniforms}
          fragmentShader={fragmentShader}
          vertexShader={vertexShader}
          // blending={THREE.MultiplyBlending}
        />
      </mesh>
    )
  }

  useEffect(() => {
    switch (stage.id) {
      case 1:
        setCameraPosition([0, 20, isMobile ? 90 : 75])
        setCameraTarget([0, -10, 0])
        setAutoAnimateModel(true)
        break
      case 2:
        setCameraPosition([0, 20, isMobile ? 85 : 60])
        setCameraTarget([0, 0, 0])
        setAutoAnimateModel(true)
        break
      case 3:
        setCameraPosition([0, 0, 300])
        setCameraTarget([0, 0, 0])
        setAutoAnimateModel(false)
        break
      case 4:
        setCameraPosition([0, 20, 60])
        setCameraTarget([0, 0, 0])
        setAutoAnimateModel(false)
        break
      // Chains split
      case 5:
        setAutoAnimateModel(false)
        setCameraPosition([0, 0, 60])
        setCameraTarget([0, 0, 0])
        break
      // Zoom Chain A
      case 6:
        setAutoAnimateModel(false)
        setCameraPosition([0, 0, 60])
        setCameraTarget([0, 0, 0])
        break
      // Zoom Chain C
      case 7:
        setAutoAnimateModel(false)
        setCameraPosition([0, 5, 45])
        setCameraTarget([0, 5, 0])
        break
      // Segment view
      case 8:
        setAutoAnimateModel(false)
        setCameraPosition([0, 0, 15])
        setCameraTarget([0, 0, 0])
        break
      case 9:
        setAutoAnimateModel(false)
        setCameraPosition([0, 0, 4])
        setCameraTarget([0, 0, 0])
        break
      // Segment view with variant
      case 10:
        setAutoAnimateModel(true)
        setCameraPosition([0, 0, 15])
        setCameraTarget([0, 0, 0])
        break
      // Full view with variants
      case 11:
        setAutoAnimateModel(true)
        setCameraPosition([0, 20, 60])
        setCameraTarget([0, 0, 0])
        break
      // Table view, look somewhere else
      case 12:
        setAutoAnimateModel(true)
        setCameraPosition([0, 0, 60])
        setCameraTarget([0, -200, 0])
        break
      case 13:
        setAutoAnimateModel(true)
        setCameraPosition([0, 40, 50])
        setCameraTarget([0, 10, 0])
        break
      case 14:
        setAutoAnimateModel(true)
        setCameraPosition([0, 0, 60])
        setCameraTarget([0, 0, 0])
        break
      case 15:
        setAutoAnimateModel(true)
        setCameraPosition([0, 0, 60])
        setCameraTarget([0, -200, 0])
        break
    }
  }, [stage])

  // TODO: Don't create Color object, set new values

  return (
    <div className={styles.canvas}>
      {/*
      To change Tone mapping: https://github.com/pmndrs/react-three-fiber/issues/1547
      Default is AcesFilmicToneMapping

      const options: WebGLRendererParameters = { ... };

      <Canvas gl={options} />
      */}
      {process.env.NODE_ENV === 'production' && <Leva hidden />}
      <Canvas
        // dpr={window.devicePixelRatio}
        // linear // Automatic sRGB encoding and gamma correction The colorspace will be set to sRGB (unless linear is true)
        // frameloop='demand'
        shadows
        camera={{ fov: 65, near: 0.001, far: 5000 }}
        gl={{ alpha: false }}
      >
        <Suspense fallback={null}>
          {/* <Sky distance={450000} sunPosition={[0, 1, 0]} inclination={0} azimuth={0.25} /> */}

          {/* <Environment
          files={require('./cubemap/env.png')}
            // files={[
            //   require('./cubemap/px.png'),
            //   require('./cubemap/nx.png'),
            //   require('./cubemap/py.png'),
            //   require('./cubemap/ny.png'),
            //   require('./cubemap/pz.png'),
            //   require('./cubemap/nz.png')
            // ]}
            background
          /> */}
        </Suspense>
        <color attach='background' args={['#222233']} />
        {/* <fog attach='fog' args={['white', 20, 40]} /> */}
        {/* <Text text={stage} fontSize={2} position={0,0,15} /> */}
        <CameraAnimation
          cameraData={[...cameraPosition, ...cameraTarget]}
          stage={stage}
        />

        <Suspense fallback={null}>
          <Virus show={stage.id === 3} />
        </Suspense>

        <Suspense fallback={null}>
          <MemoizedSpike
            stage={stage}
            position={pos}
            chainsColor={{
              thickness: [
                new Vector3(colorTA.r / 255, colorTA.g / 255, colorTA.b / 255),
                new Vector3(colorTB.r / 255, colorTB.g / 255, colorTB.b / 255),
                new Vector3(colorTC.r / 255, colorTC.g / 255, colorTC.b / 255)
              ],
              diffuse: [
                new Vector3(colorDA.r / 255, colorDA.g / 255, colorDA.b / 255),
                new Vector3(colorDB.r / 255, colorDB.g / 255, colorDB.b / 255),
                new Vector3(colorDC.r / 255, colorDC.g / 255, colorDC.b / 255)
              ]
            }}
            autoAnimate={autoAnimateModel}
          />
        </Suspense>

        <AnimatedLights
          color={[
            new Color(
              lightAColor.r / 255,
              lightAColor.g / 255,
              lightAColor.b / 255
            ),
            new Color(
              lightBColor.r / 255,
              lightBColor.g / 255,
              lightBColor.b / 255
            )
          ]}
          position={[
            [lightAPos.x, lightAPos.y],
            [lightBPos.x, lightBPos.y]
          ]}
          intensity={[lightAIntensity, lightBIntensity]}
        />
        {/* <OrbitControls /> */}
        {process.env.NODE_ENV !== 'production' && <Stats />}
        <Gradient size={[1, 1]} position={[0, 0, 0]} />
        {/* <ambientLight color={0x888888} /> */}
        {/* <EnvSphereShader /> */}
        {/* <EffectComposerTest /> */}
        {/* <ParticlesEffect count={100} /> */}
        {/* <hemisphereLight intensity={1} groundColor={new Color(0xffffff)} color={new Color(0xffffff)} /> */}
      </Canvas>
    </div>
  )
}
